/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel, ErrorsResponseModel } from '../../../../../core/models';
import { LbRbacService } from '../../../shared/services/utilities';
import { LoadingService, ModalService, NotifyService } from '../../../../../core/services';
import { NetworkingService, StaticRoutesService } from '../../../shared/services/components';
import { StaticRouteModel } from '../../../shared/models';
import { StaticRoutesApiService } from '../../../shared/http';
import { TableMultiSelectModel } from '../../../../../core/models/tableMultiSelect.model';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';

@Component({
    selector: 'app-static-routes',
    templateUrl: './static-routes.component.html'
})

export class StaticRoutesComponent extends BaseComponent {
    /**
     * Table row data.
     */
    public tableRows: any[] = [];

    /**
     * Temporary list of table objects used for searching (Table ONLY).
     */
    public tempRows: any[] = [];

    /**
     * Store selected row object for table state checks.
     */
    public selectedRowObject: any = [];

    /**
     * Search term for searching the services table, also used with the search query parameter in the url
     */
    public searchTerm: string = '';

    /**
     * To store the actions and text for the tables select all functions.
     */
    public actionDotsArray: any = [];

    /**
     * Store the search keys.
     */
    public searchKeys: string[] = ['gateway', 'type', 'groupName'];

    /**
     * Store button text and action for select all action dots.
     */
    public multiSelectActions: TableMultiSelectModel[] = [];

    /**
     * Store selected row key.
     * @private
     */
    private _setSelectedKey: string;

    constructor(
        public modalService: ModalService,
        public rbacService: LbRbacService,
        private loadingService: LoadingService,
        private networkingService: NetworkingService,
        private notifyService: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
        private staticRoutesApiService: StaticRoutesApiService,
        private staticRoutesService: StaticRoutesService,
    ) {
        super();

        this.setUpPageData();
        this.setSelectAll();
    }

    /**
     * Get the selected key.
     */
    get selectedKey(): any {
        return this._setSelectedKey;
    }

    /**
     * Confirm delete ip from group
     * @param row
     */
    public confirmDeleteIndividualRoute(row: StaticRouteModel): void {
        const confirmationObject = {
            title: 'static_route_delete',
            message: `Are you sure you would like to delete static route ${row.gateway} (${row.type}) ?`,
            callBack: this.deleteRoute.bind(this, row.id),
            options: { translateText: false }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Add or edit IP.
     * @param row
     */
    public addEditRoute(row?: any): void {
        const url = row ? 'edit/' + row.id : 'add/';
        this.router.navigate([url], { relativeTo: this.route }).then();
    }

    /**
     * Trigger onselect function when table row(s) selected.
     * @param event
     */
    public onSelect(event: any): void {
        this.multiSelectActions[0]['buttonAction'] = this.confirmDeleteMultipleRoutes.bind(this, event);
    }

    /**
     * Set the select all button.
     */
    public setSelectAll(): void {
        this.multiSelectActions = [
            {
                buttonText: 'static_route_delete_selected',
                buttonAction: this.confirmDeleteMultipleRoutes.bind(this),
                buttonDisabled: false
            }
        ];
    }

    /**
     * Sets the static IP data from the API response.
     * @private
     */
    private setUpPageData(): void {
        this.staticRoutesService.getStaticRouteData()
            .pipe(takeUntil(this.destroyed$))
            .subscribe(data => {
                this.populateRows(data);
            });
    }

    /**
     * Populate table rows for displaying in the table.
     * @private
     */
    private populateRows(data: any): void {
        this.tableRows = [];
        for (const route of data['routes']) {
            const group = data['groups'].find((item: { id: any; }) => item.id === route.groupId);
            const assignedInterface = this.networkingService.getLocalAssignedInterface(group, data['interfaces']);
            this.tableRows.push({
                groupName: `${group.name} (${assignedInterface?.settings?.interfaceName})`,
                id: route.id,
                gateway: route.gateway,
                type: route.type
            });
        }

        this.tempRows = [...this.tableRows];
        this.selectedRowObject = this.tableRows.filter(row => row.id === this.selectedKey);
    }

    /**
     * Confirm deletion of multiple routes.
     * @param selectedRows
     * @private
     */
    private confirmDeleteMultipleRoutes(selectedRows: any[]): void {
        const filteredRows = selectedRows.filter(item => item.type === 'static');
        if (filteredRows?.length) {
            const message = filteredRows.length > 1
                // eslint-disable-next-line max-len
                ? `Are you sure you want to delete ${filteredRows.length} routes? ${filteredRows.length < selectedRows.length ? '(excludes the default route)' : ''}`
                : `Are you sure you want to delete route '${filteredRows[0].gateway}' ?`;
            const confirmationObject = {
                title: 'static_route_delete_multiple_routes',
                message: message,
                callBack: this.deleteMultipleRoutes.bind(this, filteredRows),
                options: { translateText: false }
            };
            this.modalService.confirmDecision(confirmationObject);
        } else {
            this.notifyService.warning('You cannot delete the default route.');
        }
    }

    /**
     * Delete multiple IPs.
     * @param selectedRows
     * @private
     */
    private deleteMultipleRoutes(selectedRows: StaticRouteModel[]): Observable<any> {
        return new Observable((observer) => {
            const dataToSubmit = from(selectedRows || []);
            const call = dataToSubmit.pipe(
                mergeMap(row => this.staticRoutesApiService.deleteRoute(row.id).pipe(catchError(error => of(error))))
            );
            call.subscribe({
                next: (response: any): void => {
                    if (response.status === 'fail') {
                        this.updateNotification(selectedRows, response.error.errors);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                },
                complete: (): void => {
                    this.setUpPageData();
                    observer.next();
                    observer.complete();
                }
            });
        });
    }

    /**
     * Delete static route.
     * @param routeId
     * @private
     */
    private deleteRoute(routeId: string): Observable<any> {
        return new Observable((observer) => {
            this.staticRoutesApiService.deleteRoute(routeId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorsResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    },
                    complete: (): void => {
                        this.loadingService.loadingActionDotsEmitter.next(false);
                    }
                });
        });
    }

    /**
     * Helper function to update the current notification with more details. e.g. ip address.
     * @param selectedRows
     * @param errors
     * @private
     */
    private updateNotification(selectedRows: any[], errors: ErrorsResponseModel): void {
        const message = errors[0]?.message;
        const value = errors[0]?.value;
        if (message && value) {
            const notification = this.notifyService.getNotificationsById(`sips-${value}-delete`);
            const sip = selectedRows.find(item => item.id === value);
            notification.message = `${sip.ip} ${message}`;
        }
    }
}
